import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import SidePanel, { SidePanelSection, SidePanelActions, SidePanelDivider } from '@/components/SidePanel';
import { API } from '@/App';
import { 
  FileSpreadsheet, Download, Calendar, Package, ChevronDown, ChevronRight, 
  FileText, IndianRupee, Receipt, Eye, MessageSquare, Search, Filter,
  CheckCircle, Clock, Send, X, Trash2, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDate } from '@/utils/dateFormat';

/**
 * VENDOR BOQ PAGE - REDESIGNED TO MATCH SUBMIT RATES UI
 * 
 * Features:
 * - Clean tabular format (matches VendorRates.jsx)
 * - Side panel for viewing details
 * - Chat function integrated
 * - Convert to Quotation action
 * - Download PDF (with vendor-filtered rates)
 * - Delete BOQ (vendor-created only)
 * 
 * VISIBILITY:
 * - Vendor sees ONLY assigned items
 * - Vendor sees ONLY vendor rate (post markdown)
 * - No base rate, client rate, or markup visibility
 */
export default function VendorBOQ() {
  const [boqWorksheets, setBoqWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBOQ, setExpandedBOQ] = useState(null);
  
  // Side Panel
  const [selectedBOQ, setSelectedBOQ] = useState(null);
  // iter-386u-d — Vendor Data Visibility: default OFF = my assigned categories only.
  const [viewFullBOQ, setViewFullBOQ] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  
  // Convert Dialog
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  
  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Chat Dialog
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchVendorBOQ();
  }, []);

  const fetchVendorBOQ = async () => {
    try {
      const response = await axios.get(`${API}/vendor/boq`);
      setBoqWorksheets(response.data);
    } catch (error) {
      console.error('Failed to fetch BOQ:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToQuotation = async () => {
    if (!selectedBOQ) return;
    
    setConverting(true);
    try {
      const response = await axios.post(`${API}/vendor/boq/${selectedBOQ.id}/convert-to-quotation`);
      toast.success(response.data.message);
      setConvertDialogOpen(false);
      setSidePanelOpen(false);
      setSelectedBOQ(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    } finally {
      setConverting(false);
    }
  };

  const handleDownloadPDF = async (boq) => {
    try {
      const response = await axios.get(`${API}/boq-worksheets/${boq.id}/export/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BOQ-${boq.worksheet_number}-vendor.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Delete BOQ handler (vendor can only delete their own BOQs)
  const handleDeleteBOQ = async () => {
    if (!selectedBOQ || !deleteReason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    
    if (deleteReason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/vendor/boq/${selectedBOQ.id}`, {
        data: { reason: deleteReason.trim() }
      });
      
      toast.success('BOQ deleted successfully');
      setDeleteDialogOpen(false);
      setDeleteReason('');
      setSidePanelOpen(false);
      setSelectedBOQ(null);
      fetchVendorBOQ();  // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete BOQ');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendChat = async () => {
    if (!selectedBOQ || !chatMessage.trim()) return;
    
    setSendingChat(true);
    try {
      await axios.post(`${API}/projects/${selectedBOQ.project_id}/chat`, {
        project_id: selectedBOQ.project_id,
        message: chatMessage,
        visibility: 'vendor',
        visible_to_vendor_id: user.id,  // Make it visible to this vendor's chat
        task_title: `BOQ: ${selectedBOQ.worksheet_number} - ${selectedBOQ.project_name}`  // Subject line for admin
      });
      
      toast.success('Message sent to admin');
      setChatDialogOpen(false);
      setChatMessage('');
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingChat(false);
    }
  };

  const openViewPanel = (boq) => {
    setSelectedBOQ(boq);
    setViewFullBOQ(false);
    setSidePanelOpen(true);
  };

  // Filter BOQs
  const filteredBOQs = boqWorksheets.filter(boq => {
    if (!searchTerm) return true;
    return (
      boq.worksheet_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      boq.project_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Calculate totals — iter-386u-m: use grand_total so the dashboard matches
  // the public /p vendor link (subtotal + GST).
  const totalValue = boqWorksheets.reduce((sum, boq) => sum + (boq.grand_total || boq.subtotal || 0), 0);
  const totalItems = boqWorksheets.reduce((sum, boq) => sum + (boq.items_count || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-boq-page">
        {/* Header - Matches VendorRates style */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 font-manrope">My BOQ</h1>
            <p className="text-stone-500 mt-1">View your assigned scope and create quotations</p>
          </div>
          
          {/* Stats Summary */}
          <div className="flex gap-4">
            <div className="text-right">
              <p className="text-sm text-stone-500">Total Items</p>
              <p className="text-xl font-bold text-stone-800">{totalItems}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-stone-500">Total Value</p>
              <p className="text-xl font-bold text-purple-600">₹{totalValue.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Search Bar - Matches VendorRates */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              placeholder="Search BOQ by number or project..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
              data-testid="search-boq"
            />
          </div>
        </div>

        {/* BOQ Table - Clean Tabular Format */}
        {filteredBOQs.length === 0 ? (
          <div className="apple-card p-12 text-center">
            <FileSpreadsheet size={48} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-600 mb-2">No BOQ Assignments</h3>
            <p className="text-stone-400 text-sm">
              BOQ items will appear here once you are assigned scope on a project.
            </p>
          </div>
        ) : (
          <div className="apple-card overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 p-4 bg-stone-50 border-b border-stone-100 text-sm font-medium text-stone-500 uppercase tracking-wider">
              <div className="col-span-3">BOQ #</div>
              <div className="col-span-3">Project</div>
              <div className="col-span-2 text-center">Items</div>
              <div className="col-span-2 text-right">Total</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            
            {/* Table Rows */}
            <div className="divide-y divide-stone-100">
              {filteredBOQs.map((boq) => (
                <div
                  key={boq.id}
                  className="hover:bg-stone-50 cursor-pointer transition-colors"
                  data-testid={`boq-row-${boq.id}`}
                >
                <div
                  className="grid grid-cols-12 gap-4 p-4 items-center"
                  onClick={() => openViewPanel(boq)}
                >
                  {/* BOQ # */}
                  <div className="col-span-3 flex items-center gap-3">
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <FileSpreadsheet size={18} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">{boq.worksheet_number}</p>
                      <p className="text-xs text-stone-400">
                        {formatDate(new Date(boq.created_at))}
                      </p>
                    </div>
                  </div>
                  
                  {/* Project */}
                  <div className="col-span-3">
                    <p className="text-stone-700">{boq.project_name}</p>
                  </div>
                  
                  {/* Items Count */}
                  <div className="col-span-2 text-center">
                    <Badge className="bg-stone-100 text-stone-600">
                      {boq.items_count} items
                    </Badge>
                  </div>
                  
                  {/* Total — iter-386u-m: display grand_total (incl. GST) */}
                  <div className="col-span-2 text-right">
                    <p className="font-semibold text-purple-600">
                      ₹{Number(boq.grand_total || boq.subtotal || 0).toLocaleString('en-IN')}
                    </p>
                    <p className="text-[10px] text-stone-400">incl. {boq.gst_rate ?? 18}% GST</p>
                  </div>
                  
                  {/* Actions */}
                  <div className="col-span-2 flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openViewPanel(boq)}
                      className="text-stone-500 hover:text-purple-600"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setSelectedBOQ(boq); setChatDialogOpen(true); }}
                      className="text-stone-500 hover:text-blue-600"
                      title="Chat with Admin"
                    >
                      <MessageSquare size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownloadPDF(boq)}
                      className="text-stone-500 hover:text-green-600"
                      title="Download PDF"
                    >
                      <Download size={16} />
                    </Button>
                  </div>
                </div>
                {/* iter-386u-h — task #3: Public-link card (mirrors ClientBOQ.jsx) */}
                {boq.public_token && (
                  <div className="mx-4 mb-4 p-2 bg-stone-50 border border-stone-200 rounded flex items-center gap-2" onClick={(e) => e.stopPropagation()} data-testid={`public-token-block-${boq.id}`}>
                    <ExternalLink size={12} className="text-stone-400" />
                    <input
                      readOnly
                      value={`${window.location.origin}/public/boq/${boq.public_token}`}
                      className="flex-1 bg-transparent text-[11px] font-mono text-stone-700 outline-none truncate"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigator.clipboard.writeText(`${window.location.origin}/public/boq/${boq.public_token}`).then(() => toast.success('Public link copied'), () => toast.error('Copy failed'))}
                      data-testid={`copy-public-token-${boq.id}`}
                    >
                      Copy
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`${window.location.origin}/public/boq/${boq.public_token}`, '_blank', 'noopener,noreferrer')}
                      title="Open public BOQ in a new tab"
                      data-testid={`open-public-token-${boq.id}`}
                    >
                      <ExternalLink size={14} />
                    </Button>
                  </div>
                )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Side Panel - View Details */}
        <SidePanel
          isOpen={sidePanelOpen}
          onClose={() => { setSidePanelOpen(false); setSelectedBOQ(null); }}
          title={selectedBOQ?.worksheet_number || 'BOQ Details'}
          subtitle={selectedBOQ?.project_name}
        >
          {selectedBOQ && (
            <>
              <SidePanelSection title="Summary">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-stone-500">Items</p>
                    <p className="font-medium text-stone-800">{selectedBOQ.items_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Subtotal</p>
                    <p className="font-medium text-stone-800">₹{Number(selectedBOQ.subtotal || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">GST ({selectedBOQ.gst_rate ?? 18}%)</p>
                    <p className="font-medium text-stone-800">₹{Number(selectedBOQ.gst_amount || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Grand Total</p>
                    <p className="font-bold text-purple-600" data-testid="vendor-boq-grand-total">₹{Number(selectedBOQ.grand_total || selectedBOQ.subtotal || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Created</p>
                    <p className="font-medium text-stone-800">
                      {formatDate(new Date(selectedBOQ.created_at))}
                    </p>
                  </div>
                </div>
              </SidePanelSection>
              
              <SidePanelDivider />
              
              <SidePanelSection title="Line Items">
                {/* iter-386u-d — Vendor Data Visibility toggle. Defaults to
                    the categories where at least one item is assigned to
                    the vendor; toggle ON to view the whole BOQ. */}
                {(() => {
                  const assignedCats = new Set(
                    (selectedBOQ.items || [])
                      .filter((i) => i.assigned_to_me)
                      .map((i) => i.main_category_id)
                      .filter(Boolean),
                  );
                  const visible = viewFullBOQ || assignedCats.size === 0
                    ? (selectedBOQ.items || [])
                    : (selectedBOQ.items || []).filter((i) => !i.main_category_id || assignedCats.has(i.main_category_id));
                  return (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-stone-500">
                          Showing {visible.length} of {(selectedBOQ.items || []).length} items
                          {!viewFullBOQ && assignedCats.size > 0 && ` · ${assignedCats.size} of your categor${assignedCats.size === 1 ? 'y' : 'ies'}`}
                        </p>
                        <button
                          type="button"
                          onClick={() => setViewFullBOQ((v) => !v)}
                          className={`text-[11px] px-2 py-1 rounded font-semibold ${viewFullBOQ ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
                          data-testid="vendor-boq-view-full-toggle"
                        >
                          {viewFullBOQ ? 'Showing Entire BOQ · Show My Categories' : 'View Entire BOQ'}
                        </button>
                      </div>
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {visible.map((item, idx) => (
                          <div key={item.id || idx} className={`p-3 rounded-lg border ${item.assigned_to_me ? 'bg-purple-50 border-purple-200' : 'bg-stone-50 border-stone-200'}`}>
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="font-semibold text-stone-900 text-sm">{item.task_title || item.description || 'Line item'}</p>
                              {item.assigned_to_me && (
                                <span className="text-[9px] uppercase font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded whitespace-nowrap">Assigned</span>
                              )}
                            </div>
                            {(item.main_category_name || item.sub_category_name) && (
                              <p className="text-[11px] text-stone-500 mb-1">
                                {[item.main_category_name, item.sub_category_name].filter(Boolean).join(' · ')}
                              </p>
                            )}
                            {item.description && item.description !== item.task_title && (
                              <p className="text-xs text-stone-600 whitespace-pre-wrap mb-2 line-clamp-4">{item.description}</p>
                            )}
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-stone-500">
                                {item.quantity} {item.unit} × ₹{item.rate?.toLocaleString('en-IN')}
                              </span>
                              <span className="font-semibold text-purple-600">
                                ₹{item.amount?.toLocaleString('en-IN')}
                              </span>
                            </div>
                          </div>
                        ))}
                        {visible.length === 0 && (
                          <p className="text-xs text-stone-400 italic text-center py-6">No matching items — toggle above to view the entire BOQ.</p>
                        )}
                      </div>
                    </>
                  );
                })()}
              </SidePanelSection>
              
              <SidePanelDivider />
              
              <SidePanelActions>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setChatDialogOpen(true); }}
                >
                  <MessageSquare size={16} className="mr-2" />
                  Chat with Admin
                </Button>
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={() => setConvertDialogOpen(true)}
                >
                  <Receipt size={16} className="mr-2" />
                  Create Quotation
                </Button>
                {/* Delete button - only for vendor-created BOQs */}
                {selectedBOQ?.can_delete && (
                  <Button
                    variant="outline"
                    className="text-red-600 hover:bg-red-50 border-red-200"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete
                  </Button>
                )}
              </SidePanelActions>
            </>
          )}
        </SidePanel>

        {/* Convert to Quotation Dialog */}
        <AlertDialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Convert to Quotation</AlertDialogTitle>
              <AlertDialogDescription>
                This will create a quotation from your assigned BOQ items.
                {selectedBOQ && (
                  <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                    <p className="font-medium text-stone-800">{selectedBOQ.worksheet_number}</p>
                    <p className="text-sm text-stone-600">{selectedBOQ.items_count} items | ₹{selectedBOQ.subtotal?.toLocaleString()}</p>
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConvertToQuotation}
                disabled={converting}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {converting ? 'Creating...' : 'Create Quotation'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Chat Dialog */}
        <Dialog open={chatDialogOpen} onOpenChange={setChatDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="text-blue-500" size={20} />
                Chat about BOQ
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedBOQ && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-stone-700">Regarding:</p>
                  <p className="text-stone-900 font-semibold">{selectedBOQ.worksheet_number}</p>
                  <p className="text-xs text-stone-500">{selectedBOQ.project_name}</p>
                </div>
              )}
              
              <div>
                <Label className="text-stone-900">Your Message</Label>
                <Textarea
                  placeholder="Type your question or comment about this BOQ..."
                  className="bg-white text-stone-900 border-stone-300 mt-1 min-h-[100px]"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setChatDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendChat}
                disabled={sendingChat || !chatMessage.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sendingChat ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete BOQ Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 size={20} />
                Delete BOQ Worksheet
              </AlertDialogTitle>
              <AlertDialogDescription>
                <p className="mb-3">
                  This action cannot be undone. Please provide a reason for deleting this BOQ.
                </p>
                {selectedBOQ && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200 mb-4">
                    <p className="font-medium text-stone-800">{selectedBOQ.worksheet_number}</p>
                    <p className="text-sm text-stone-600">{selectedBOQ.items_count} items | ₹{selectedBOQ.subtotal?.toLocaleString()}</p>
                  </div>
                )}
                <div>
                  <Label className="text-stone-700">Reason for deletion *</Label>
                  <Textarea
                    placeholder="e.g., Created by mistake, Duplicate entry, Project scope changed..."
                    className="mt-1 min-h-[80px]"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                  />
                  <p className="text-xs text-stone-500 mt-1">Minimum 5 characters required</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteReason('')}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteBOQ}
                disabled={deleting || deleteReason.trim().length < 5}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? 'Deleting...' : 'Delete BOQ'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
